const React = require('react');
const T = require('prop-types');
const { useCallback, useEffect, useRef, useState } = React;
const { context } = require('app-context');
const { getDisplayImageUrl } = require('utils/image');

const { default: Box } = require('@mui/material/Box');
const { default: Typography } = require('@mui/material/Typography');
const { default: TextField } = require('@mui/material/TextField');
const { default: Button } = require('@mui/material/Button');
const { default: Alert } = require('@mui/material/Alert');
const { default: CircularProgress } = require('@mui/material/CircularProgress');
const { default: Paper } = require('@mui/material/Paper');
const { default: CloudUploadIcon } = require('@mui/icons-material/CloudUpload');

const api = context.api.nearpeer;

const getPreviewSrc = (storedUrl, localPreviewUrl) => {

    if (localPreviewUrl) {
        return localPreviewUrl;
    }

    if (!storedUrl) {
        return null;
    }

    return getDisplayImageUrl(storedUrl) || storedUrl;
};

const ResourceImageField = React.forwardRef(function ResourceImageField(props, ref) {

    const {
        disabled,
        error,
        helperText,
        id,
        label,
        name,
        onBlur,
        onChange,
        onFocus,
        required,
        setValue,
        value
    } = props;

    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [localPreviewUrl, setLocalPreviewUrl] = useState(null);
    const previewSrc = getPreviewSrc(value, localPreviewUrl);
    const hasError = !!error;
    const errorMessage = helperText || error?.message || null;
    const isDisabled = !!disabled || uploading;

    useEffect(() => () => {

        if (localPreviewUrl) {
            URL.revokeObjectURL(localPreviewUrl);
        }
    }, [localPreviewUrl]);

    const clearLocalPreview = useCallback(() => {

        setLocalPreviewUrl((current) => {

            if (current) {
                URL.revokeObjectURL(current);
            }

            return null;
        });
    }, []);

    const commitValue = useCallback((nextValue, event) => {

        if (typeof onChange === 'function') {
            onChange(nextValue, event);
        }

        if (typeof setValue === 'function') {
            setValue(nextValue, event);
        }
    }, [onChange, setValue]);

    const handleSelectFile = useCallback(async (event) => {

        const file = event.target.files?.[0];

        if (!file) {
            return;
        }

        clearLocalPreview();
        setLocalPreviewUrl(URL.createObjectURL(file));
        setUploading(true);
        setUploadError(null);

        try {
            const { imageUrl: uploadedUrl } = await api.resources.adminUploadImage({ resourceImage: file });
            commitValue(uploadedUrl);
            clearLocalPreview();
        }
        catch (err) {
            setUploadError(err.message || 'Failed to upload image');
            clearLocalPreview();
        }
        finally {
            setUploading(false);
            event.target.value = '';
        }
    }, [clearLocalPreview, commitValue]);

    const handleUrlChange = useCallback((event) => {

        setUploadError(null);
        commitValue(event.target.value, event);
    }, [commitValue]);

    return (
        <Box sx={{ mb: 1 }}>
            <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 1 }}>
                {label}{required ? ' *' : ''}
            </Typography>

            {previewSrc && (
                <Paper variant='outlined' sx={{ p: 1.5, mb: 1.5, display: 'inline-flex' }}>
                    <Box
                        component='img'
                        src={previewSrc}
                        alt={`${label} preview`}
                        sx={{
                            maxHeight: 80,
                            maxWidth: 160,
                            objectFit: 'contain',
                            display: 'block'
                        }}
                    />
                </Paper>
            )}

            <input
                ref={fileInputRef}
                type='file'
                accept='image/*'
                hidden
                disabled={isDisabled}
                onChange={handleSelectFile}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <Button
                    size='small'
                    variant='outlined'
                    startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isDisabled}
                >
                    {uploading ? 'Uploading…' : (value ? 'Replace image' : 'Upload image')}
                </Button>
                <Typography variant='caption' color='text.secondary'>
                    PNG, JPG, WEBP, or GIF up to 10MB
                </Typography>
            </Box>

            {uploadError && <Alert severity='error' sx={{ mb: 1.5 }}>{uploadError}</Alert>}

            <TextField
                inputRef={ref}
                id={id}
                name={name}
                label='Image URL'
                value={value || ''}
                onChange={handleUrlChange}
                onBlur={onBlur}
                onFocus={onFocus}
                disabled={isDisabled}
                error={hasError}
                helperText={errorMessage}
                required={!!required}
                fullWidth
                variant='filled'
            />
        </Box>
    );
});

ResourceImageField.propTypes = {
    disabled: T.bool,
    error: T.oneOfType([T.bool, T.object]),
    helperText: T.string,
    id: T.string,
    label: T.string,
    name: T.string,
    onBlur: T.func,
    onChange: T.func,
    onFocus: T.func,
    required: T.bool,
    setValue: T.func,
    value: T.string
};

module.exports = ResourceImageField;
